// src/components/icons.tsx
// Lightweight stroke-based icon set. 24x24 viewBox, currentColor stroke.
import React from 'react';

type IconProps = { size?: number; className?: string; style?: React.CSSProperties; strokeWidth?: number };

const base = (size: number, sw: number): React.SVGProps<SVGSVGElement> => ({
  width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: sw, strokeLinecap: 'round', strokeLinejoin: 'round',
  'aria-hidden': true,
});

export const IconDashboard = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <rect x="3" y="3" width="7" height="9" rx="2" />
    <rect x="14" y="3" width="7" height="5" rx="2" />
    <rect x="14" y="12" width="7" height="9" rx="2" />
    <rect x="3" y="16" width="7" height="5" rx="2" />
  </svg>
);

export const IconLive = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <circle cx="12" cy="12" r="3" />
    <path d="M5.5 5.5a9 9 0 0 0 0 13" />
    <path d="M18.5 5.5a9 9 0 0 1 0 13" />
    <path d="M8 8a5 5 0 0 0 0 8" />
    <path d="M16 8a5 5 0 0 1 0 8" />
  </svg>
);

export const IconBoard = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <rect x="3" y="4" width="18" height="14" rx="2" />
    <path d="M7 9h10M7 13h6" />
  </svg>
);

export const IconFeed = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M3 12a9 9 0 0 1 9 9" />
    <path d="M3 6a15 15 0 0 1 15 15" />
    <circle cx="4.5" cy="19.5" r="1.5" />
  </svg>
);

export const IconMarket = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M3 7l2-3h14l2 3" />
    <path d="M4 7v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7" />
    <path d="M9 11a3 3 0 1 0 6 0" />
  </svg>
);

export const IconUser = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </svg>
);

export const IconTransfer = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M4 7h13M14 4l3 3-3 3" />
    <path d="M20 17H7M10 20l-3-3 3-3" />
  </svg>
);

export const IconYouth = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <circle cx="12" cy="6" r="3" />
    <path d="M5 21v-2a7 7 0 0 1 14 0v2" />
    <path d="M9 13l3 2 3-2" />
  </svg>
);

export const IconClub = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M3 21V9l9-6 9 6v12" />
    <path d="M9 21v-7h6v7" />
  </svg>
);

export const IconTrophy = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M7 4h10v5a5 5 0 0 1-10 0V4z" />
    <path d="M5 6H3a2 2 0 0 0 0 4h2M19 6h2a2 2 0 0 1 0 4h-2" />
    <path d="M9 21h6M12 14v7" />
  </svg>
);

export const IconAuction = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M14 4l6 6-3 3-6-6 3-3z" />
    <path d="M11 7l-7 7 3 3 7-7" />
    <path d="M5 21h14" />
  </svg>
);

export const IconDispute = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M12 3v18" />
    <path d="M5 8l-3 5h6L5 8zM19 8l-3 5h6l-3-5z" />
    <path d="M2 13a3 3 0 0 0 6 0M16 13a3 3 0 0 0 6 0" />
    <path d="M5 21h14" />
  </svg>
);

export const IconVenue = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <ellipse cx="12" cy="12" rx="9" ry="6" />
    <ellipse cx="12" cy="12" rx="4" ry="2.5" />
  </svg>
);

export const IconWhistle = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M3 12a6 6 0 0 0 12 0 6 6 0 0 0-12 0z" />
    <path d="M15 10l6-4v8l-6-2" />
  </svg>
);

export const IconCloud = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M7 18a4 4 0 1 1 .6-7.97A6 6 0 0 1 19 12a3 3 0 0 1 0 6H7z" />
    <path d="M8 21l-1 2M12 21l-1 2M16 21l-1 2" />
  </svg>
);

export const IconCard = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <rect x="2" y="6" width="20" height="13" rx="2" />
    <path d="M2 10h20M6 15h4" />
  </svg>
);

export const IconBell = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2.5h-15L6 16z" />
    <path d="M10 20a2 2 0 0 0 4 0" />
  </svg>
);

export const IconSun = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);

export const IconMoon = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" />
  </svg>
);

export const IconMenu = ({ size = 22, strokeWidth = 2, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M4 7h16M4 12h16M4 17h10" />
  </svg>
);

export const IconClose = ({ size = 22, strokeWidth = 2, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const IconArrowRight = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

export const IconChevronLeft = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M15 6l-6 6 6 6" />
  </svg>
);

export const IconChevronRight = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

export const IconLogout = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </svg>
);

export const IconCricketBat = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M14.5 3l6.5 6.5-9 9-6.5-6.5L14.5 3z" />
    <path d="M5 16l-2 2 3 3 2-2" />
    <circle cx="18" cy="18" r="1.5" />
  </svg>
);

export const IconSparkle = ({ size = 16, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
    <path d="M19 16l.7 2L22 18.7l-2.3.3L19 21l-.7-2L16 18.7l2.3-.7L19 16z" />
  </svg>
);

export const IconWorld = ({ size = 18, strokeWidth = 1.8, ...rest }: IconProps) => (
  <svg {...base(size, strokeWidth)} {...rest}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z" />
  </svg>
);
